import { META } from "@/seo/routeMeta";
import { thinkers } from "@/data/nonlinearThinkers";
import { foundingMembers } from "@/data/foundingMembers";

export type SearchItem = {
  title: string;
  subtitle?: string;
  keywords?: string;
  /** internal route or hash */
  to?: string;
  /** external url */
  href?: string;
  group: "Pages" | "Home sections" | "Nonlinear thinkers" | "Founding members" | "Elsewhere";
};

const EXTRA_PAGES: SearchItem[] = [
  { title: "Membership Overview", subtitle: "Compare all membership tiers", to: "/membership", group: "Pages" },
  { title: "Become a Partner", subtitle: "Partner with the movement", to: "/become-a-partner", group: "Pages" },
  { title: "Project Octopus", subtitle: "The project", to: "/project-octopus", group: "Pages" },
  { title: "Stories", subtitle: "Stories from the Human Mycelium", to: "/stories", group: "Pages" },
  { title: "Who Holds the Space", subtitle: "Stewards, hosts and leads", to: "/who-holds-the-space", group: "Pages" },
  { title: "Founding Members Family", subtitle: "Private space for founding members", to: "/founding-members-family", group: "Pages" },
  { title: "Connect on WhatsApp", subtitle: "All tOM WhatsApp groups", keywords: "whatsapp groups conversations spark jar need a brain quick wins podcasts", to: "/whatsapp", group: "Pages" },
  { title: "Shop", subtitle: "Stickers for nonlinear minds", keywords: "stickers merchandise tom shop", to: "/shop", group: "Pages" },
];

const HOME_SECTIONS: SearchItem[] = [
  { title: "How It Works", subtitle: "The mechanism", to: "/#how-it-works", group: "Home sections" },
  { title: "The Organism", subtitle: "How the movement is built", to: "/#organism", group: "Home sections" },
  { title: "Stories", subtitle: "The evidence", to: "/#evidence", group: "Home sections" },
  { title: "Alive Now", subtitle: "What is happening right now", to: "/#alive-now", group: "Home sections" },
  { title: "Who Holds the Space", subtitle: "The people holding the rooms", to: "/#who-holds-the-space", group: "Home sections" },
  { title: "Find Your Way In", subtitle: "Doors into the movement", to: "/#find-your-way-in", group: "Home sections" },
];

const ELSEWHERE: SearchItem[] = [
  { title: "Join the Movement", subtitle: "Sign-up form", href: "https://airtable.com/appj9naWTSEyBxyub/shrahOKBrQHWbDb65", group: "Elsewhere" },
  { title: "The Engine Room", subtitle: "Members platform", href: "https://members.theoctopusmovement.org/home", group: "Elsewhere" },
  { title: "tOM Community (Disco)", subtitle: "Community platform", href: "https://theoctopusmovement.disco.co/join", group: "Elsewhere" },
  { title: "Think Tank Institute", subtitle: "octopus-thinktank.institute", href: "https://octopus-thinktank.institute/", group: "Elsewhere" },
  { title: "iBUILD", subtitle: "ibuild.theoctopusmovement.org", href: "https://ibuild.theoctopusmovement.org/", group: "Elsewhere" },
];

const HIDDEN_ROUTES = new Set(["/wall-admin", "/stories-admin", "/surprise"]);

function titleFromMeta(title: string) {
  return title.split("|")[0].trim();
}

const metaPages: SearchItem[] = Object.entries(META)
  .filter(([path]) => path !== "/" && !HIDDEN_ROUTES.has(path))
  .map(([path, meta]) => ({
    title: titleFromMeta(meta.title),
    subtitle: meta.description,
    to: path,
    group: "Pages" as const,
  }));

const thinkerItems: SearchItem[] = thinkers.map((t) => ({
  title: t.name,
  subtitle: `${t.field} · ${t.era}`,
  keywords: [t.description, ...t.tags].join(" "),
  to: "/nonlinear-thinkers",
  group: "Nonlinear thinkers",
}));

const memberItems: SearchItem[] = foundingMembers.map((m) => ({
  title: m.name,
  subtitle: `${m.jobTitle} · ${m.country}`,
  to: "/founding-members",
  group: "Founding members",
}));

const dedupe = (items: SearchItem[]) => {
  const seen = new Set<string>();
  return items.filter((i) => {
    const key = `${i.group}|${i.title}|${i.to ?? i.href}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const searchIndex: SearchItem[] = dedupe([
  { title: "Home", subtitle: "12,000 minds. One human mycelium.", to: "/", group: "Pages" },
  ...metaPages,
  ...EXTRA_PAGES,
  ...HOME_SECTIONS,
  ...thinkerItems,
  ...memberItems,
  ...ELSEWHERE,
]);